"use client";

import { cn } from "@/lib/utils";
import { type ReactNode, type ButtonHTMLAttributes } from "react";

type NeonColor = "pink" | "cyan" | "purple" | "blue" | "green" | "red" | "orange";

interface CyberButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "solid" | "outline" | "ghost";
  color?: NeonColor;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
}

const colorConfig: Record<NeonColor, { solid: string; outline: string; ghost: string; glow: string }> = {
  pink: {
    solid: "bg-neon-pink text-white",
    outline: "border-neon-pink text-neon-pink",
    ghost: "text-neon-pink hover:bg-neon-pink/10",
    glow: "hover:shadow-[0_0_20px_rgba(255,16,240,0.4),0_0_40px_rgba(255,16,240,0.15)]",
  },
  cyan: {
    solid: "bg-neon-cyan text-void-black",
    outline: "border-neon-cyan text-neon-cyan",
    ghost: "text-neon-cyan hover:bg-neon-cyan/10",
    glow: "hover:shadow-[0_0_20px_rgba(0,255,255,0.4),0_0_40px_rgba(0,255,255,0.15)]",
  },
  purple: {
    solid: "bg-neon-purple text-white",
    outline: "border-neon-purple text-neon-purple",
    ghost: "text-neon-purple hover:bg-neon-purple/10",
    glow: "hover:shadow-[0_0_20px_rgba(189,0,255,0.4),0_0_40px_rgba(189,0,255,0.15)]",
  },
  blue: {
    solid: "bg-neon-blue text-white",
    outline: "border-neon-blue text-neon-blue",
    ghost: "text-neon-blue hover:bg-neon-blue/10",
    glow: "hover:shadow-[0_0_20px_rgba(0,128,255,0.4),0_0_40px_rgba(0,128,255,0.15)]",
  },
  green: {
    solid: "bg-neon-green text-void-black",
    outline: "border-neon-green text-neon-green",
    ghost: "text-neon-green hover:bg-neon-green/10",
    glow: "hover:shadow-[0_0_20px_rgba(57,255,20,0.4),0_0_40px_rgba(57,255,20,0.15)]",
  },
  red: {
    solid: "bg-neon-red text-white",
    outline: "border-neon-red text-neon-red",
    ghost: "text-neon-red hover:bg-neon-red/10",
    glow: "hover:shadow-[0_0_20px_rgba(255,7,58,0.4),0_0_40px_rgba(255,7,58,0.15)]",
  },
  orange: {
    solid: "bg-neon-orange text-white",
    outline: "border-neon-orange text-neon-orange",
    ghost: "text-neon-orange hover:bg-neon-orange/10",
    glow: "hover:shadow-[0_0_20px_rgba(255,107,0,0.4),0_0_40px_rgba(255,107,0,0.15)]",
  },
};

const sizeConfig = {
  sm: "px-3 py-1.5 text-xs gap-1.5",
  md: "px-5 py-2.5 text-sm gap-2",
  lg: "px-7 py-3.5 text-base gap-2.5",
};

export function CyberButton({
  children,
  variant = "solid",
  color = "cyan",
  size = "md",
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = "left",
  className,
  disabled,
  ...props
}: CyberButtonProps) {
  const config = colorConfig[color];

  return (
    <button
      className={cn(
        "relative inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-300",
        "font-[family-name:var(--font-space-grotesk)] uppercase tracking-wider",
        "disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none",
        sizeConfig[size],
        variant === "solid" && config.solid,
        variant === "outline" && `border-2 bg-transparent ${config.outline}`,
        variant === "ghost" && config.ghost,
        !disabled && config.glow,
        fullWidth && "w-full",
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {!loading && icon && iconPosition === "left" && (
        <span className="shrink-0">{icon}</span>
      )}
      <span>{children}</span>
      {!loading && icon && iconPosition === "right" && (
        <span className="shrink-0">{icon}</span>
      )}
    </button>
  );
}
